import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import getPosts, { type Post } from "../content/loadPosts";
import "./BlogList.css";

const BLOG_DESCRIPTION =
  "ASDRY environmental field reports and practical insight into how weather affects real-world plans and activities.";
const DEFAULT_DESCRIPTION =
  "ASDRY Beta turns weather and environmental data into practical preparation guidance for running, walking, commuting, cycling, dog walks, gardening, and outdoor plans.";

const BlogList: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);

  const insights = posts.filter((p) => p.type === "insight");
  const fieldTests = posts.filter((p) => p.type === "field-test");
  const otherPosts = posts.filter(
    (p) => !p.type || p.type === "article"
  );

  useEffect(() => {
    getPosts().then((data) => setPosts(data ?? [])).catch(() => setPosts([]));
  }, []);

  useEffect(() => {
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!description) return;
    description.content = BLOG_DESCRIPTION;
    return () => {
      description.content = DEFAULT_DESCRIPTION;
    };
  }, []);

  return (
    <div className="page">
      <Helmet>
        <title>ASDRY Environmental Insights &amp; Field Reports</title>
        <link rel="canonical" href="https://asdry.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="ASDRY Environmental Insights & Field Reports" />
        <meta property="og:description" content={BLOG_DESCRIPTION} />
        <meta property="og:url" content="https://asdry.com/blog" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="ASDRY Environmental Insights & Field Reports" />
        <meta name="twitter:description" content={BLOG_DESCRIPTION} />
      </Helmet>
      <div className="page-container">
        <header className="page-header">
          <h1 className="page-title">Environmental Field Reports & Operational Insight</h1>
          <p className="page-intro">
            Field reports and analysis from the ASDRY engine — translating environmental conditions into structured preparation insight and operational understanding.
          </p>
        </header>

        <div className="card-stack">

          {fieldTests.length > 0 && (
            <>
              <h2 className="section-title">Field Reports</h2>
              {fieldTests.map(post => (
                <article key={post.slug} className="content-card">
                  <h2 className="content-title">
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </h2>

                  <div className="content-meta">
                    <span>{post.author}</span>
                    <span>·</span>
                    <span>{post.date}</span>
                    {post.readingTime && (
                      <>
                        <span>·</span>
                        <span>{post.readingTime} min read</span>
                      </>
                    )}
                  </div>

                  {post.excerpt && (
                    <p className="content-description">
                      {post.excerpt}
                    </p>
                  )}
                </article>
              ))}
            </>
          )}

          {insights.length > 0 && (
            <>
              <h2 className="section-title">Insights</h2>
              {insights.map(post => (
                <article key={post.slug} className="content-card">
                  <h2 className="content-title">
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </h2>

                  <div className="content-meta">
                    <span>{post.author}</span>
                    <span>·</span>
                    <span>{post.date}</span>
                    {post.readingTime && (
                      <>
                        <span>·</span>
                        <span>{post.readingTime} min read</span>
                      </>
                    )}
                  </div>

                  {post.excerpt && (
                    <p className="content-description">
                      {post.excerpt}
                    </p>
                  )}
                </article>
              ))}
            </>
          )}

          {otherPosts.length > 0 && (
            <>
              <h2 className="section-title">Articles</h2>
              {otherPosts.map(post => (
                <article key={post.slug} className="content-card">
                  <h2 className="content-title">
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </h2>

                  <div className="content-meta">
                    <span>{post.author}</span>
                    <span>·</span>
                    <span>{post.date}</span>
                    {post.readingTime && (
                      <>
                        <span>·</span>
                        <span>{post.readingTime} min read</span>
                      </>
                    )}
                  </div>

                  {post.excerpt && (
                    <p className="content-description">
                      {post.excerpt}
                    </p>
                  )}
                </article>
              ))}
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default BlogList;
